/* eslint-disable */
import { image } from '~/js/utils/image';
import {
  convertWishlistProduct,
  convertRelewiseProduct,
} from '../lib/product-object-converter';

/**
 * Builds sale-price literal
 * @returns {TemplateLiteral}
 */
export const salePrice = (product) => {
  let html;

  if (product.price < product.compare_at_price) {
    html = `
      <del data-update="stl-old-price" class="text-warm-gray-400">
        ${Shopify.formatMoney(product.compare_at_price, theme.moneyFormat)}
      </del>
    `;
  } else {
    html = '<del data-update="stl-old-price"></del>';
  }

  return html;
};

/**
 * Builds price literal
 * @returns {TemplateLiteral}
 */
export const regPrice = (product) => {
  return `
    <span data-update="stl-price" class="font-semibold text-h3 ${
      product.price < product.compare_at_price ? 'text-primary' : ''
    }">
      ${Shopify.formatMoney(product.price, theme.moneyFormat)}
    </span>
  `;
};

/**
 * Build Product HTML
 * @param {Object} product
 * @param {String} context - card | line | quick
 * @param {String} origin
 * @param {String} sectionId
 * @param {String} handle
 */
export function buildProduct(
  product,
  context = 'card',
  origin,
  sectionId,
  handle
) {
  /**
   * Builds 2nd (hover) image
   * @returns {Function} image.js - 2nd image
   */
  const extraImage = () => {
    if (product.images && product.images.length > 1) {
      const imgObject = {
        src: product.images[1],
        alt: '',
        height: 400,
        width: 400,
      };

      return image(
        imgObject,
        'image image-second opacity-0 group-hover:opacity-100'
      );
    } else return '';
  };

  /**
   * Builds product images
   * @returns {Function | String} image.js or placeholder
   */
  const images = () => {
    if (product.images.length) {
      let imgClass = 'image transition-opacity';

      if (product.images.length > 1) {
        imgClass = imgClass + ' lg:group-hover:opacity-0';
      }

      const imgObject = {
        src: product.images[0],
        alt: '',
        height: 400,
        width: 400,
      };

      return `
        ${image(imgObject, imgClass)}
        ${extraImage()}
      `;
    } else {
      return `<img class="border !h-full border-basics-medium-grey" src="https://cdn.shopify.com/s/files/1/0567/6643/1398/files/no-image.jpg?v=1623402346" alt="placeholder"/>`;
    }
  };

  /**
   * @description build card Pills
   * @param { Object } product - product item
   * @returns { HTML } Pills markup
   */
  const buildPills = () => {
    const buildNewPill = () => {
      const showNewLabel = window.theme?.pfs?.showNewLabel == 'true' || false;
      if (!showNewLabel) return '';

      const thresholdInDays = Number(window.theme?.pfs?.thresholdInDays || 0);
      const createdAt = product.created_at * 1000;

      const dayInMiliseconds = 1000 * 60 * 60 * 24;

      const dates = {
        start: new Date(createdAt),
        end: new Date(new Date().setDate(new Date().getDate())),
      };

      const createdDaysAgo = Math.round(
        (dates.end - dates.start) / dayInMiliseconds
      );
      const isNew = createdDaysAgo - thresholdInDays < 0;

      if (!isNew) return '';

      return `
        <span class="label bg-sand-400">
          ${window.theme?.pfs?.newLabelText || 'new'}
        </span>
      `;
    };

    const buildSalePill = () => {
      const showSaleLabel = window.theme?.pfs?.showSaleLabel == 'true' || false;
      if (!showSaleLabel) return '';

      const price = product.price,
        comp_price = product.compare_at_price;

      if (price < comp_price) {
        const diff = comp_price - price;
        const discount = `${Math.round(
          (((diff / 100) * 100) / comp_price) * 100
        )}%`;
        const activeCampaign = theme.discountPeriods.some((period) => {
          return period.tags.some((tag) => {
            if (product.tags.find((t) => t === tag)) {
              const end = new Date(period.end_date);

              return end > new Date();
            }

            return false;
          });
        });

        let text = window.theme.pfs.saleLabelText.replace(
          '{{ discount }}',
          discount
        );

        if (activeCampaign) {
          text = window.theme.pfs.salesCampaignLabelText.replace(
            '{{ discount }}',
            discount
          );
        }

        return `
          <span class="label bg-sand-400">
            ${text}
          </span>
        `;
      } else return '';
    };

    const buildTagPill = () => {
      const tags = product?.tags?.filter((tag) => tag.includes('label:'));
      let pills = '';

      if (tags && tags.length) {
        tags.forEach((tag) => {
          const label = tag.replace('label:', '');
          pills += `
            <span class="label bg-sand-400">${label}</span>
          `;
        });
      }

      return pills;
    };

    return `
      <div class="item-pills">
        ${buildNewPill()}
        ${buildSalePill()}
        ${buildTagPill()}
      </div>

      ${buildDiscountPeriod({ returnIcon: true })}
    `;
  };

  // TODO: inset discount period icons above this live som where

  /**
   * Builds "more variants" pill
   * @returns {TemplateLiteral}
   */
  const buildVariantPill = () => {
    const linkedTag = product?.tags?.find((tag) => tag.includes('linked:'));
    let pill = '';

    if (linkedTag) {
      pill = `
        <span class="label-variants">
          ${window.theme?.strings?.moreVariants || ''}
        </span>
      `;
    }

    return pill;
  };

  /**
   * Builds "promoted" label + modal (if product is promoted)
   * @returns {TemplateLiteral}
   */
  const promoted = () => {
    if (!product.promoted) {
      return '';
    }

    const id = `promoted-${product.id}-${new Date().getTime()}`;

    return `
      <button
        type="button"
        class="absolute left-0 bottom-0 px-[6px] py-1 flex items-center gap-1 text-warm-gray-400"
        aria-haspopup="true"
        aria-expanded="false"
        aria-controls="${id}"
        data-promoted-toggle
      >
        <span class="font-heading text-[10px] lg:text-12 font-semibold">${
          theme.strings.promotedContent.label
        }</span>

        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 15" class="size-[16px]" fill="none">
          <line x1="7" y1="5.2" x2="7" y2="7.5" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" stroke="currentColor"/>
          <line x1="7" y1="9.8" x2="7" y2="9.8" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" stroke="currentColor"/>
          <circle cx="7" cy="7.5" r="5.8" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" stroke="currentColor"/>
        </svg>
      </button>

      <div
        class="product-item__promoted"
        id="${id}"
        hidden
        data-promoted
      >
        <div class="product-item__promoted__content">
          <header class="h-[54px] shrink-0 px-[32px] flex items-center justify-between">
            <span class="font-bold">${
              theme.strings.promotedContent.label
            }</span>

            <button
              type="button"
              class="flex items-center gap-1"
              data-promoted-close
            >
              <span>${theme.strings.close}</span>

              <svg class="size-[20px]" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.8125 6.1875L6.1875 15.8125M15.8125 15.8125L6.1875 6.1875L15.8125 15.8125Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </header>

          <div class="grow px-[32px] py-[20px] overflow-x-hidden overflow-y-auto leading-[1.3]">
            <a
              href="${
                handle ? `/collections/${handle}${product.url}` : product.url
              }"
              class="border border-gray-100 p-[10px] flex items-center gap-[16px]"
            >
              <div class="w-[84px]">
                <div
                  class="block aspect-ratio aspect-ratio--1/1"
                >
                  ${image({
                    src: product.images[0],
                    alt: '',
                    height: 84,
                    width: 84,
                  })}
                </div>
              </div>

              <div>
                <p class="font-semibold text-sand-800">${product.vendor}</p>
                <p class="mt-[4px] text-warm-gray-500">${product.title}</p>
              </div>
            </a>

            <div class="mt-[32px] rte promoted-content text-warm-gray-500 [&_strong]:text-black">
              ${theme.products.promotedContentDescription.replace(
                /{{ vendor }}/g,
                product.vendor
              )}
            </div>
          </div>

          <footer class="px-[32px] py-[20px]">
            <a href="${
              handle ? `/collections/${handle}${product.url}` : product.url
            }" class="button button--buy w-full py-5">
              ${theme.strings.promotedContent.showProduct}
            </a>
          </footer>
        </div>
      </div>
    `;
  };

  /**
   * Escapes attribute values for Lipscore data attributes
   * @param {unknown} value
   * @returns {string}
   */
  const escapeAttr = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');

  const getProductUrl = () => {
    const path = handle ? `/collections/${handle}${product.url}` : product.url;
    return path.startsWith('http') ? path : `${window.location.origin}${path}`;
  };

  const getFirstVariant = () => {
    const variant = product.variants?.[0];
    return typeof variant === 'object' ? variant : null;
  };

  const getSkuList = () => {
    if (!product.variants?.length) return '';

    return [...new Set(
      product.variants
        .map((variant) => (typeof variant === 'object' ? variant.sku : '') || '')
        .filter(Boolean),
    )].join(';');
  };

  const getImageUrl = () => {
    const raw = product.images?.[0];
    if (!raw) return '';

    let src = raw;
    if (typeof src === 'object' && src !== null) {
      src = src.src ?? src.url ?? '';
      if (typeof src === 'object' && src !== null) {
        src = src.src ?? '';
      }
    }

    if (!src || typeof src !== 'string') return '';
    if (src.startsWith('//')) return `https:${src}`;
    return src;
  };

  /**
   * Lipscore PDP-style rating markup — mirrors snippets/rating-lipscore.liquid (card)
   * @returns {TemplateLiteral}
   */
  const lipscoreRating = () => {
    const variant = getFirstVariant();
    const title = product.title || product.name || '';
    const price = product.price != null ? (product.price / 100).toFixed(2) : '';

    return `
      <span
        class="lipscore-rating-small"
        data-ls-product-name="${escapeAttr(title)}"
        data-ls-brand="${escapeAttr(product.vendor)}"
        data-ls-product-id="${escapeAttr(product.id)}"
        data-ls-product-url="${escapeAttr(getProductUrl())}"
        data-ls-variant-id="${escapeAttr(variant?.id || '')}"
        data-ls-variant-name="${escapeAttr(variant?.title || '')}"
        data-ls-sku="${escapeAttr(getSkuList())}"
        data-ls-description=""
        data-ls-image-url="${escapeAttr(getImageUrl())}"
        data-ls-price="${escapeAttr(price)}"
        data-ls-price-currency="${escapeAttr(Shopify.currency?.active || '')}"
        data-ls-availability="${product.available ? '1' : '0'}"
        data-ls-category=""
        data-ls-gtin="${escapeAttr(variant?.barcode || '')}"
      ></span>
    `;
  };

  /**
   * Vendor row with Lipscore rating
   * @returns {TemplateLiteral}
   */
  const vendorRow = () => `
    <div class="ls-vendor-row" data-component="lipscore-rating-small">
      ${lipscoreRating()}
      ${vendor()}
    </div>
  `;

  /**
   * Builds vendor link
   * @returns {TemplateLiteral}
   */
  const vendor = () => {
    if (product.vendor_collection) {
      return `
        <a
          class="item-body-vendor item-body-vendor--link"
          href="${product.within_url}">
          ${product.vendor}
        </a>
      `;
    }

    return `
      <p class="item-body-vendor">
        ${product.vendor}
      </p>
    `;
  };

  /**
   * Adds wishlist attributes
   * @returns {TemplateLiteral}
   */
  const wishlistAttributes = () => {
    if (origin === 'wishlist') {
      return `data-wishlist-item='${product.wishlist_item_id}'`;
    } else return '';
  };

  const getFirstVariantId = () => {
    // will most likely always be an object, since all
    // products using this method either have variants from Shopify,
    // or gets fetched variants from Shopify API.
    return typeof product.variants[0] === 'object'
      ? product.variants[0].id
      : product.variants[0];
  };

  const uniqueId = () => {
    let id = product.handle;

    if (sectionId) {
      id += sectionId;
    }

    return id;
  };

  /**
   * Builds quick actions
   * @returns {TemplateLiteral}
   */
  const quickBuyActions = ({ wkIcon }) => {
    const wk = () => `
      <div class="mt-4 ml-2 relative"
        data-wishlist-button>
      </div>
    `;

    const buttonTmpl = (attr) => `
    <div class="flex w-full">
      <button ${attr} class="w-full mt-4 button button--buy p-0">
        <svg class="icon hidden md:inline-block h-6 w-6" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.875 7.5625V6.1875C6.875 5.09348 7.3096 4.04427 8.08318 3.27068C8.85677 2.4971 9.90598 2.0625 11 2.0625C12.094 2.0625 13.1432 2.4971 13.9168 3.27068C14.6904 4.04427 15.125 5.09348 15.125 6.1875V7.5625M3.4375 7.5625C3.25516 7.5625 3.0803 7.63493 2.95136 7.76386C2.82243 7.8928 2.75 8.06766 2.75 8.25V17.5312C2.75 18.8306 3.85688 19.9375 5.15625 19.9375H16.8437C18.1431 19.9375 19.25 18.8843 19.25 17.585V8.25C19.25 8.06766 19.1776 7.8928 19.0486 7.76386C18.9197 7.63493 18.7448 7.5625 18.5625 7.5625H3.4375Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>

        ${theme.strings.addToCart}
      </button>

      ${wkIcon ? wk() : ''}
      </div>
    `;

    if (product.variants && product.available) {
      if (product.variants.length > 1) {
        return `
          <div class="flex w-full">
          <a href="${
            handle ? `/collections/${handle}${product.url}` : product.url
          }" class="w-full mt-4 button button--buy p-0">
            <svg class="icon hidden md:inline-block h-6 w-6" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.875 7.5625V6.1875C6.875 5.09348 7.3096 4.04427 8.08318 3.27068C8.85677 2.4971 9.90598 2.0625 11 2.0625C12.094 2.0625 13.1432 2.4971 13.9168 3.27068C14.6904 4.04427 15.125 5.09348 15.125 6.1875V7.5625M3.4375 7.5625C3.25516 7.5625 3.0803 7.63493 2.95136 7.76386C2.82243 7.8928 2.75 8.06766 2.75 8.25V17.5312C2.75 18.8306 3.85688 19.9375 5.15625 19.9375H16.8437C18.1431 19.9375 19.25 18.8843 19.25 17.585V8.25C19.25 8.06766 19.1776 7.8928 19.0486 7.76386C18.9197 7.63493 18.7448 7.5625 18.5625 7.5625H3.4375Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>

            ${theme.strings.quickBuyPopupSelectVariant}
          </a>

            ${wkIcon ? wk() : ''}
          </div>
        `;
      } else {
        return `
          <form
            method="post"
            class="flex w-full"
            action="/cart/add"
            id="product_form_${product.id}"
            accept-charset="UTF-8"
            enctype="multipart/form-data"
            data-action="stl-submit">

            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">

            <input type="hidden" name="id" value="${getFirstVariantId()}">

            ${buttonTmpl(
              'data-update="stl-submit-button" data-add-to-cart-btn'
            )}
          </form>
        `;
      }
    }
    return '';
  };

  /**
   * Builds remove-item icon on wishlist items
   * @param {String} origin
   * @returns {TemplateLiteral}
   */
  const wishlistRemove = () => {
    if (origin === 'wishlist') {
      return `
        <div class="absolute right-6 top-6 z-10">
          <button class="wk-button" data-remove-wishlist-item>
            <svg class="icon cursor-pointer" viewBox="0 0 140 140" height="140" width="140" xmlns="http://www.w3.org/2000/svg">
              <g transform="matrix(5.833333333333333,0,0,5.833333333333333,0,0)">
                <path class="text-black" d="M1.5 4.5L22.5 4.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
                <path class="text-black" d="M14.25,1.5H9.75A1.5,1.5,0,0,0,8.25,3V4.5h7.5V3A1.5,1.5,0,0,0,14.25,1.5Z" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
                <path class="text-black" d="M9.75 17.25L9.75 9.75" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
                <path class="text-black" d="M14.25 17.25L14.25 9.75" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
                <path class="text-black" d="M18.865,21.124A1.5,1.5,0,0,1,17.37,22.5H6.631a1.5,1.5,0,0,1-1.495-1.376L3.75,4.5h16.5Z" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
              </g>
            </svg>
          </button>
        </div>
      `;
    }
    return '';
  };

  /**
   * Outputs 'group' class on items where hover should be used
   * @returns {TemplateLiteral}
   */
  const imageGroup = () => {
    if (origin !== 'wishlist') return 'group';
    return '';
  };

  const upsellCardAction = () => {
    if (product.variants.length > 1) {
      return `
        <a href="${
          handle ? `/collections/${handle}${product.url}` : product.url
        }" class="w-full font-semibold mt-4 button button--white">
          ${theme.strings.exploreProduct}
        </a>
      `;
    } else {
      return `
        <button
          class="w-full font-semibold mt-4 button button--white"
          data-product-id="${product.id}"
          data-variant-id="${product.variants[0].id}"
          data-change
          data-add-to-cart-btn
          data-href="${window.theme.url}/cart/add?id=${product.variants[0].id}&quantity=1">

          <svg class="icon h-6 w-6 mr-2 pointer-events-none" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.875 7.5625V6.1875C6.875 5.09348 7.3096 4.04427 8.08318 3.27068C8.85677 2.4971 9.90598 2.0625 11 2.0625C12.094 2.0625 13.1432 2.4971 13.9168 3.27068C14.6904 4.04427 15.125 5.09348 15.125 6.1875V7.5625M3.4375 7.5625C3.25516 7.5625 3.0803 7.63493 2.95136 7.76386C2.82243 7.8928 2.75 8.06766 2.75 8.25V17.5312C2.75 18.8306 3.85688 19.9375 5.15625 19.9375H16.8437C18.1431 19.9375 19.25 18.8843 19.25 17.585V8.25C19.25 8.06766 19.1776 7.8928 19.0486 7.76386C18.9197 7.63493 18.7448 7.5625 18.5625 7.5625H3.4375Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>

          ${theme.strings.addToCart}
        </button>
      `;
    }
  };

  const buildUpsellCard = () => `
    <div class="relative w-1/2 mb-4 flex">

      <div class="p-2 flex flex-col w-full">
        <div class="border border-warm-gray-100 flex-1 flex w-full flex-col">
          <span class="image-wrapper">
            <a
              href="${
                handle ? `/collections/${handle}${product.url}` : product.url
              }"
              class="block aspect-ratio aspect-ratio--1/1 bg-white ${imageGroup()}">
              ${images()}
            </a>
          </span>

          <div class="p-4 text-[1.2rem] font-heading flex-1 flex flex-col">
            ${vendorRow()}

            <p class="item-body-title mb-auto">
              <a
                class="line-clamp-2 no-underline text-warm-gray-500 leading-tight"
                href="${
                  handle ? `/collections/${handle}${product.url}` : product.url
                }">
                ${product.name}
              </a>
            </p>

            <p class="item-body-prices space-x-2 mt-4 font-heading flex text-sm flex-col sm:flex-row">
              ${regPrice(product)}
              ${salePrice(product)}
            </p>

          </div>
        </div>

        ${upsellCardAction()}
      </div>
    </div>
  `;

  const buildDiscountPeriod = ({ returnIcon = false }) => {
    let text = '';
    let icon = '';

    const formatDate = (date) => {
      return new Intl.DateTimeFormat(
        `${Shopify.locale.split('-')[0]}-${Shopify.country}`,
        {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        }
      ).format(date);
    };

    theme.discountPeriods.forEach((period) => {
      period.tags.forEach((tag) => {
        if (product.tags.find((t) => t == tag)) {
          const start = new Date(period.start_date);
          const end = new Date(period.end_date);

          if (end > new Date()) {
            text = theme.strings.discountPeriod
              .replace('start', formatDate(start))
              .replace('end', formatDate(end));

            if (returnIcon && period.icon) {
              icon = `<img class="absolute z-10 top-4 right-4 w-auto h-8" src="${period.icon}" alt="${period.name}"/>`;
            }
          }
        }
      });
    });

    if (returnIcon) {
      if (!icon) {
        return '';
      }
      return icon;
    } else {
      return text ? `<div class="text-xs mt-4">${text}</div>` : '';
    }
  };

  const buildCard = () => `
    <div
      data-component="product-item"
      data-id="${product.id}"
      class="product-item product-item--card relative h-full"
      ${wishlistAttributes()}>

      <span class="image-wrapper">
        ${buildPills()}
        ${wishlistRemove()}
        <span>
          <a
            href="${
              handle ? `/collections/${handle}${product.url}` : product.url
            }"
            class="block aspect-ratio aspect-ratio--1/1 bg-white ${imageGroup()}">
            ${images()}
          </a>

          ${buildVariantPill()}
        </span>

        ${promoted()}
      </span>

      <div class="item-body">
        ${vendorRow()}

        <a
          class="flex-grow flex flex-col"
          href="${
            handle ? `/collections/${handle}${product.url}` : product.url
          }">

          <p class="item-body-title block">
            <span class="line-clamp-2" href="${
              handle ? `/collections/${handle}${product.url}` : product.url
            }">
              ${product.title}
            </span>
          </p>

          <p class="item-body-prices mt-auto flex flex-col font-heading sm:flex-row">
            ${regPrice(product)}
            ${salePrice(product)}
          </p>
        </a>

        ${quickBuyActions({ wkIcon: true })}
        ${buildDiscountPeriod({ returnIcon: false })}
      </div>
    </div>
  `;

  const buildCardWithAltLayout = () => buildCard();

  switch (context) {
    case 'quick':
      return buildCardWithAltLayout();

    case 'upsell':
      return buildUpsellCard();

    default:
      return buildCard();
  }
}

/**
 * Builds category links
 * @param {Array} products
 * @param {String} context - 'card | quick'
 * @param {String} origin
 * @param {String} sectionIdOrIndex - Can be sectionId or index depending on usage
 */
export function buildProductItems(
  products,
  context = 'card',
  origin = 'none',
  sectionIdOrIndex
) {
  let html = '';

  if (products) {
    products.forEach((product, index) => {
      let convertedProduct = product;

      switch (origin) {
        case 'wishlist':
          convertedProduct = {
            ...convertWishlistProduct(product),
            promoted: product.promoted ?? false,
          };

          break;

        case 'relewise':
          convertedProduct = {
            ...convertRelewiseProduct(product),
            promoted: product.promoted ?? false,
          };

          break;

        default:
          break;
      }

      // Determine if sectionIdOrIndex is a sectionId or index based on origin
      let sectionId, indexParam;

      if (origin === 'relewise') {
        // For relewise origin, the 4th parameter is the sectionId
        sectionId = sectionIdOrIndex;
        indexParam = index;
      } else {
        // For other origins, the 4th parameter is sectionId
        sectionId = sectionIdOrIndex;
        indexParam = index;
      }

      let item = buildProduct(
        convertedProduct,
        context,
        origin,
        sectionId,
        indexParam
      );

      if (
        sectionId &&
        (sectionId.includes('relewise-product-slider') ||
          sectionId.includes('product-suggestions'))
      ) {
        item = `<div class="swiper-slide !h-auto">${item}</div>`;
      }

      html += item;
    });

    document.dispatchEvent(new Event('app:remount'));
  }

  return html;
}
